import React from 'react';
import { t } from '@lingui/macro';
import styled from 'styled-components';

import Badge from '@decisiv/ui-components/lib/components/Badge';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H3 } from '@decisiv/ui-components/lib/components/Typography';
import ExclamationTriangle from '@decisiv/iconix/lib/components/ExclamationTriangle';

import ROUTE_NAMES from 'setup/Routing/routeNames';

import Card from 'components/DashboardCard';

import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';

import Loading from 'components/Loading';
import Divider from 'components/Divider';

import isNull from 'lodash/isNull';
import { Grid } from './styles';

import LinkOrLabel from '../LinkOrLabel';
import SeverityItem from './SeverityItem';

const dashboardFaultsBySeverityQuery = loader(
  './dashboardFaultsBySeverity.graphql',
);

const Paper = styled(Flex).attrs({ padding: 2 })`
  width: 100%;
  box-shadow: 0 1px 4px rgba(37, 38, 46, 0.4);
  border-radius: 2px;
`;

const getCountText = (loading, total) => {
  if (loading) return '';

  return isNull(total) ? '―' : total;
};

const getBadgeColor = (total) => {
  return isNull(total) ? 'licoriceMousse' : 'information';
};

const infoText = t`View the total number of faults organized by fault severity.`;

export default function FaultsBySeverityCard() {
  const {
    loading,
    data: {
      dashboardFaultsBySeverity: {
        total,
        severe,
        moderate,
        attention,
        unknown,
      } = {},
    } = {},
  } = useQuery(dashboardFaultsBySeverityQuery);

  const totalFaultsLink = `${ROUTE_NAMES.alerts}?alertTypes=faults`;
  const severeFaultsLink = `${ROUTE_NAMES.alerts}?alertTypes=faults&faultStatus=SEVERE`;
  const moderateFaultsLink = `${ROUTE_NAMES.alerts}?alertTypes=faults&faultStatus=MODERATE`;
  const attentionFaultsLink = `${ROUTE_NAMES.alerts}?alertTypes=faults&faultStatus=ATTENTION`;
  const unknownFaultsLink = `${ROUTE_NAMES.alerts}?alertTypes=faults&faultStatus=UNKNOWN`;

  const totalFaultsLabel = t`Fault Alerts`;
  const severeFaultsLabel = t`Severe`;
  const moderateFaultsLabel = t`Moderate`;
  const attentionFaultsLabel = t`Attention`;
  const unknownFaultsLabel = t`Unknown`;

  return (
    <Card text={t`Faults by Severity`} infoText={infoText} marginTop={0}>
      {loading && <Loading />}
      {!loading && (
        <Flex flexDirection="row">
          <Paper marginY={1}>
            <Grid>
              <Flex alignItems="top">
                <Badge
                  variant="square"
                  icon={ExclamationTriangle}
                  color={getBadgeColor(total)}
                />
              </Flex>
              <Flex flexDirection="column" marginLeft={1}>
                <H3 weight="regular">{getCountText(loading, total)}</H3>
                <LinkOrLabel
                  count={total}
                  to={totalFaultsLink}
                  text={totalFaultsLabel}
                />
              </Flex>
              <Divider
                css={`
                  height: auto;
                `}
                marginX={1}
              />
              <Flex flexDirection="column" marginLeft={1} marginBottom={2}>
                <Flex flexDirection="column" marginLeft={1}>
                  <SeverityItem
                    severity="severe"
                    count={severe}
                    loading={loading}
                    to={severeFaultsLink}
                    text={severeFaultsLabel}
                  />
                </Flex>
                <Flex flexDirection="column" marginLeft={1} marginTop={3}>
                  <SeverityItem
                    severity="attention"
                    count={attention}
                    loading={loading}
                    to={attentionFaultsLink}
                    text={attentionFaultsLabel}
                  />
                </Flex>
              </Flex>
              <Flex flexDirection="column" marginLeft={1}>
                <Flex flexDirection="column" marginLeft={1}>
                  <SeverityItem
                    severity="moderate"
                    count={moderate}
                    loading={loading}
                    to={moderateFaultsLink}
                    text={moderateFaultsLabel}
                  />
                </Flex>
                <Flex flexDirection="column" marginLeft={1} marginTop={3}>
                  <SeverityItem
                    severity="unknown"
                    count={unknown}
                    loading={loading}
                    to={unknownFaultsLink}
                    text={unknownFaultsLabel}
                  />
                </Flex>
              </Flex>
            </Grid>
          </Paper>
        </Flex>
      )}
    </Card>
  );
}
